import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Typography, Box, FormControl, InputLabel, Select, MenuItem,
    CircularProgress, Alert, Stack, FormHelperText
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import api from '../utils/Api';

const UploadGlobalSbaModal = ({ open, onClose }) => {
    const [templateKey, setTemplateKey] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setError(null);
        setSuccess(null);
    };

    const handleUpload = async () => {
        if (!templateKey) {
            setError("Please select a template key.");
            return;
        }
        if (!file) {
            setError("Please select a file to upload.");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', templateKey);

            const res = await api.post('/sba/admin/upload-template', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSuccess(res.data.message || "Global template uploaded successfully!");
            setFile(null);
            
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Failed to upload global template.");
        } finally {
            setLoading(false);
        }
    };

    const standardKeys = [
        { value: 'CRECHE', label: 'Creche / Daycare' },
        { value: 'NURSERY_1', label: 'Nursery 1' },
        { value: 'NURSERY_2', label: 'Nursery 2' },
        { value: 'KG_1', label: 'KG 1' },
        { value: 'KG_2', label: 'KG 2' },
        { value: 'BASIC_1', label: 'Basic 1 (Primary 1)' },
        { value: 'BASIC_2', label: 'Basic 2 (Primary 2)' },
        { value: 'BASIC_3', label: 'Basic 3 (Primary 3)' },
        { value: 'BASIC_4', label: 'Basic 4 (Primary 4)' },
        { value: 'BASIC_5', label: 'Basic 5 (Primary 5)' },
        { value: 'BASIC_6', label: 'Basic 6 (Primary 6)' },
        { value: 'BASIC_7', label: 'Basic 7 (JHS 1)' },
        { value: 'BASIC_8', label: 'Basic 8 (JHS 2)' },
        { value: 'BASIC_9', label: 'Basic 9 (JHS 3)' }
    ];

    return (
        <Dialog open={open} onClose={loading ? null : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Global SBA Template</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Upload a global SBA master template. This template will be used by ALL schools that do not have their own custom template for this class level.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <FormControl fullWidth size="small">
                        <InputLabel>Template Key (Class Level)</InputLabel>
                        <Select
                            value={templateKey}
                            label="Template Key (Class Level)"
                            onChange={(e) => setTemplateKey(e.target.value)}
                        >
                            {standardKeys.map(k => (
                                <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Select the global class level this template applies to</FormHelperText>
                    </FormControl>

                    <Box sx={{ border: '2px dashed #1976d2', p: 3, textAlign: 'center', borderRadius: 2, bgcolor: '#f5f9ff' }}>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            id="global-sba-upload"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <label htmlFor="global-sba-upload">
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<CloudUpload />}
                                disabled={loading}
                            >
                                Select Global Master Excel File
                            </Button>
                        </label>
                        {file && (
                            <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
                                Selected: {file.name}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    color="secondary"
                    disabled={loading || !file || !templateKey}
                    startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
                >
                    {loading ? "Uploading..." : "Upload Global Template"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadGlobalSbaModal;
